import { useState, useEffect } from 'react';
import { authFetch } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  HelpCircle,
  MessageSquare,
  Briefcase,
  Calculator,
  BarChart3,
  Radar,
  Lightbulb,
  FileText,
  Users,
  Sparkles,
  Vote,
  Send,
  Loader2,
  CheckCircle2,
  ArrowRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const activeTools = [
  {
    href: '/advisor',
    icon: MessageSquare,
    title: 'Asesor Laboral IA',
    description: 'Consulta situaciones laborales como cambios de contrato, prestaciones, o problemas con tu empleador.',
    color: 'bg-blue-500',
  },
  {
    href: '/job-evaluator',
    icon: Briefcase,
    title: 'Evaluador de Oferta',
    description: 'Analiza ofertas de trabajo: salario, prestaciones, bonos, y calcula tu compensación total.',
    color: 'bg-emerald-500',
  },
  {
    href: '/severance',
    icon: Calculator,
    title: 'Calculadora de Liquidación',
    description: 'Estima tu finiquito y liquidación según la Ley Federal del Trabajo de México.',
    color: 'bg-amber-500',
  },
  {
    href: '/salary-benchmark',
    icon: BarChart3,
    title: 'Benchmark Salarial',
    description: 'Compara tu salario con el mercado mexicano en tu industria y nivel de experiencia.',
    color: 'bg-purple-500',
  },
];

const comingSoonFeatures = [
  { name: 'Radar de Carrera', icon: Radar, description: 'Descubre qué habilidades y movimientos pueden aumentar tu valor profesional' },
  { name: 'Tu idea', icon: Lightbulb, description: 'Sugiere la herramienta que necesitas' },
  { name: 'Optimizador de CV y LinkedIn', icon: FileText, description: 'Optimiza tu CV y tu perfil de LinkedIn para mejores oportunidades' },
  { name: 'Bolsa de Trabajo', icon: Users, description: 'Encuentra oportunidades laborales relevantes para tu perfil' },
];

export default function GuidancePage() {
  const [selectedVote, setSelectedVote] = useState('');
  const [voteSummary, setVoteSummary] = useState([]);
  const [feedbackProblem, setFeedbackProblem] = useState('');
  const [feedbackTool, setFeedbackTool] = useState('');
  const [submittingVote, setSubmittingVote] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [registeredInterests, setRegisteredInterests] = useState([]);

  useEffect(() => {
    fetchVoteSummary();
  }, []);

  const fetchVoteSummary = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/features/votes/summary`);
      if (response.ok) {
        const data = await response.json();
        setVoteSummary(data);
      }
    } catch (error) {
      console.error('Error fetching vote summary:', error);
    }
  };

  const handleVote = async () => {
    if (!selectedVote) return;
    setSubmittingVote(true);

    try {
      await authFetch(`${API_URL}/api/features/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ feature_name: selectedVote }),
      });
      toast.success('¡Gracias por tu voto!');
      fetchVoteSummary();
    } catch (error) {
      toast.error('Error al votar');
    } finally {
      setSubmittingVote(false);
    }
  };

  const handleFeedback = async (e) => {
    e.preventDefault();
    if (!feedbackProblem && !feedbackTool) return;
    setSubmittingFeedback(true);

    try {
      await authFetch(`${API_URL}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          problem_description: feedbackProblem,
          desired_tool: feedbackTool,
        }),
      });
      toast.success('¡Gracias por tu feedback!');
      setFeedbackProblem('');
      setFeedbackTool('');
    } catch (error) {
      toast.error('Error al enviar feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleInterest = async (featureName) => {
    try {
      await authFetch(`${API_URL}/api/features/interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ feature_name: featureName }),
      });
      setRegisteredInterests((prev) => [...prev, featureName]);
      toast.success('Interés registrado');
    } catch (error) {
      console.error('Error registering interest:', error);
    }
  };

  const getTotalVotes = () => voteSummary.reduce((acc, v) => acc + v.votes, 0);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-8" data-testid="guidance-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-[#0F172A] font-['Manrope'] flex items-center gap-3">
            <HelpCircle className="w-8 h-8 text-[#2563EB]" />
            ¿Qué hago?
          </h1>
          <p className="mt-2 text-muted-foreground text-lg">
            Guía completa sobre cómo Klarvi puede ayudarte en tu carrera.
          </p>
        </div>

        {/* Section 1: Active Tools */}
        <section>
          <h2 className="text-2xl font-semibold text-[#0F172A] font-['Manrope'] mb-4">
            1. Herramientas Activas
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {activeTools.map((tool) => {
              const Icon = tool.icon;
              return (
                <Link key={tool.href} to={tool.href}>
                  <Card className="border h-full hover:shadow-lg hover:border-[#2563EB]/30 transition-all cursor-pointer">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-xl ${tool.color} flex items-center justify-center flex-shrink-0`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-[#0F172A] font-['Manrope']">{tool.title}</h3>
                          <p className="text-sm text-muted-foreground mt-1">{tool.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </section>

        <Separator />

        {/* Section 2: How Klarvi Helps */}
        <section>
          <h2 className="text-2xl font-semibold text-[#0F172A] font-['Manrope'] mb-4">
            2. ¿Cómo puede ayudarte Klarvi?
          </h2>
          <Card className="border">
            <CardContent className="p-6">
              <div className="space-y-4 text-muted-foreground">
                <p>
                  <strong className="text-[#0F172A]">Klarvi</strong> es una plataforma de apoyo para decisiones laborales diseñada para empleados y profesionales en México.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h4 className="font-medium text-[#0F172A]">Puedes usar Klarvi para:</h4>
                    <ul className="space-y-1 text-sm">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                        Entender situaciones laborales complejas
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                        Evaluar ofertas de trabajo objetivamente
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                        Calcular tu liquidación estimada
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                        Comparar tu salario con el mercado
                      </li>
                    </ul>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-medium text-[#0F172A]">Lo que Klarvi NO hace:</h4>
                    <ul className="space-y-1 text-sm">
                      <li className="flex items-center gap-2">
                        <span className="w-4 h-4 text-[#EF4444]">✕</span>
                        Proporcionar asesoramiento legal vinculante
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-4 h-4 text-[#EF4444]">✕</span>
                        Representación legal ante autoridades
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-4 h-4 text-[#EF4444]">✕</span>
                        Ofrecer bolsa de trabajo o vacantes
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-4 h-4 text-[#EF4444]">✕</span>
                        Asesoría fiscal o financiera definitiva
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <Separator />

        {/* Section 3: Coming Soon */}
        <section>
          <h2 className="text-2xl font-semibold text-[#0F172A] font-['Manrope'] mb-4 flex items-center gap-2">
            3. Funciones en Desarrollo
            <Badge variant="secondary">
              <Sparkles className="w-3 h-3 mr-1" />
              Próximamente
            </Badge>
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {comingSoonFeatures.map((feature) => {
              const Icon = feature.icon;
              const isInterested = registeredInterests.includes(feature.name);
              return (
                <Card key={feature.name} className="border bg-muted/30">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-muted-foreground font-['Manrope']">{feature.name}</h3>
                        <p className="text-sm text-muted-foreground/70 mt-1">{feature.description}</p>
                        <Button
                          variant={isInterested ? 'secondary' : 'outline'}
                          size="sm"
                          className="mt-3"
                          onClick={() => handleInterest(feature.name)}
                          disabled={isInterested}
                          data-testid={`interest-${feature.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {isInterested ? 'Interés registrado' : 'Me interesa'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        <Separator />

        {/* Section 4: Feature Priority Poll */}
        <section>
          <h2 className="text-2xl font-semibold text-[#0F172A] font-['Manrope'] mb-4 flex items-center gap-2">
            <Vote className="w-6 h-6 text-[#2563EB]" />
            4. Encuesta de Prioridades
          </h2>
          <Card className="border">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope']">
                ¿Qué funcionalidad te gustaría que desarrollemos primero?
              </CardTitle>
              <CardDescription>
                Tu voto nos ayuda a priorizar el desarrollo de nuevas funciones.
                {getTotalVotes() > 0 && ` (${getTotalVotes()} votos totales)`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup value={selectedVote} onValueChange={setSelectedVote} className="space-y-3">
                {comingSoonFeatures.map((feature) => {
                  const voteData = voteSummary.find((v) => v.feature === feature.name);
                  const voteCount = voteData?.votes || 0;
                  const percentage = getTotalVotes() > 0 ? Math.round((voteCount / getTotalVotes()) * 100) : 0;
                  
                  return (
                    <div key={feature.name} className="flex items-center space-x-3">
                      <RadioGroupItem
                        value={feature.name}
                        id={feature.name}
                        data-testid={`vote-${feature.name.toLowerCase().replace(/\s+/g, '-')}`}
                      />
                      <Label htmlFor={feature.name} className="flex-1 cursor-pointer">
                        <div className="flex items-center justify-between">
                          <span>{feature.name}</span>
                          {voteCount > 0 && (
                            <Badge variant="outline" className="ml-2">
                              {voteCount} ({percentage}%)
                            </Badge>
                          )}
                        </div>
                      </Label>
                    </div>
                  );
                })}
              </RadioGroup>
              <Button
                onClick={handleVote}
                disabled={!selectedVote || submittingVote}
                className="mt-4 bg-[#2563EB] hover:bg-[#2563EB]/90"
                data-testid="submit-vote-btn"
              >
                {submittingVote ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Votar
              </Button>
            </CardContent>
          </Card>
        </section>

        <Separator />

        {/* Section 5: Feedback */}
        <section>
          <h2 className="text-2xl font-semibold text-[#0F172A] font-['Manrope'] mb-4">
            5. Tu Feedback
          </h2>
          <Card className="border">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope']">Cuéntanos más</CardTitle>
              <CardDescription>
                Tu opinión nos ayuda a mejorar Klarvi.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleFeedback} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="problem">¿Qué problema estás tratando de resolver?</Label>
                  <Textarea
                    id="problem"
                    value={feedbackProblem}
                    onChange={(e) => setFeedbackProblem(e.target.value)}
                    placeholder="Describe tu situación o problema..."
                    rows={3}
                    data-testid="feedback-problem-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tool">¿Qué herramienta te ayudaría más?</Label>
                  <Textarea
                    id="tool"
                    value={feedbackTool}
                    onChange={(e) => setFeedbackTool(e.target.value)}
                    placeholder="Describe la herramienta o función que te gustaría ver..."
                    rows={3}
                    data-testid="feedback-tool-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={(!feedbackProblem && !feedbackTool) || submittingFeedback}
                  className="bg-[#0F172A] hover:bg-[#0F172A]/90"
                  data-testid="submit-feedback-btn"
                >
                  {submittingFeedback ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Enviar Feedback
                </Button>
              </form>
            </CardContent>
          </Card>
        </section>

        {/* CTA */}
        <Card className="border bg-gradient-to-r from-[#0F172A] to-[#1E293B]">
          <CardContent className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="text-white">
                <h3 className="text-xl font-semibold font-['Manrope']">
                  ¿Listo para empezar?
                </h3>
                <p className="mt-1 text-slate-300">
                  Explora nuestras herramientas y toma mejores decisiones laborales.
                </p>
              </div>
              <Link to="/dashboard">
                <Button className="bg-white text-[#0F172A] hover:bg-white/90" data-testid="go-dashboard-btn">
                  Ir al Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
